import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const from = location.state?.from || "/admin";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0c10] flex items-center justify-center px-6">
      <div className="w-full max-w-md border border-[#24283b] bg-[#0e121c] p-8 md:p-10 shadow-[0_0_50px_rgba(69,243,255,0.08)]">
        <div className="flex flex-col items-center mb-8">
          <img src="/goat-mark.jpg" alt="GOAT Productions" className="h-14 mb-4" />
          <span className="font-mono-goat text-xs uppercase tracking-widest text-[#45f3ff]">
            Studio Access
          </span>
          <h1 className="font-display text-3xl uppercase tracking-tight text-white mt-1">
            Admin Login
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
          <div>
            <label
              htmlFor="login-email"
              className="block font-mono-goat text-[11px] uppercase tracking-wider text-neutral-300 mb-2"
            >
              Email
            </label>
            <input
              id="login-email"
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#161a29] border border-[#24283b] text-white px-4 py-3 text-sm focus:border-[#45f3ff] focus:outline-none transition-colors"
              placeholder="you@studio.com"
            />
          </div>
          <div>
            <label
              htmlFor="login-password"
              className="block font-mono-goat text-[11px] uppercase tracking-wider text-neutral-300 mb-2"
            >
              Password
            </label>
            <input
              id="login-password"
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#161a29] border border-[#24283b] text-white px-4 py-3 text-sm focus:border-[#45f3ff] focus:outline-none transition-colors"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p
              data-testid="login-error"
              className="font-mono-goat text-xs text-red-400 border border-red-400/30 bg-red-400/10 px-3 py-2"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#45f3ff] text-black font-mono-goat font-bold text-xs uppercase tracking-widest hover:bg-white transition-colors disabled:opacity-60"
          >
            {submitting ? (
              <>
                Signing in <Loader2 className="w-3.5 h-3.5 animate-spin" />
              </>
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/"
            data-testid="login-back-home-link"
            className="font-mono-goat text-xs text-neutral-500 hover:text-[#45f3ff] transition-colors"
          >
            ← Back to site
          </Link>
        </div>
      </div>
    </div>
  );
}
